import i18next from 'i18next';
import type { Contract } from '../types';

export interface ContractBadge {
  label: string;
  /** Bootstrap background utility suffix, e.g. "success" or "warning text-dark". */
  variant: string;
}

/**
 * Version / lifecycle badge for a contract. Returns null for a plain, current
 * first version (no badge). Shared by the company and provider (jurist /
 * besiktningsman) contract views so both render the exact same version markers.
 */
export const contractVersionBadge = (contract: Contract): ContractBadge | null => {
  // A cumulative plan version does not replace the ÄTA agreement. Signing
  // and activation are shown by its draft badge and amendment notice.
  if (contract.stageVariation) return null;
  const version = contract.contractVersion ?? 1;
  const status = contract.versionStatus ?? 'current';
  if (version <= 1 && status === 'current') return null;
  switch (status) {
    case 'proposed':
      if (contract.companySigned && !contract.personSigned) {
        return {
          label: i18next.t('projectView.version.awaitingCustomer', { version }),
          variant: 'warning text-dark'
        };
      }
      if (!contract.companySigned && contract.personSigned) {
        return {
          label: i18next.t('projectView.version.awaitingCompany', { version }),
          variant: 'warning text-dark'
        };
      }
      if (contract.companySigned && contract.personSigned) {
        return {
          label: i18next.t('projectView.version.signed', { version }),
          variant: 'warning text-dark'
        };
      }
      return { label: i18next.t('projectView.version.draft', { version }), variant: 'info' };
    case 'settlement_pending':
      return {
        label: i18next.t('projectView.version.reconciliation', { version }),
        variant: 'warning text-dark'
      };
    case 'superseded':
      return { label: i18next.t('projectView.version.replaced', { version }), variant: 'secondary' };
    case 'activation_failed':
      return { label: i18next.t('projectView.version.notActivated', { version }), variant: 'danger' };
    default:
      return { label: i18next.t('projectView.version.current', { version }), variant: 'success' };
  }
};

/**
 * Bootstrap colour suffix for a contract draft/signing status. Mirrors the
 * company contract list exactly so the provider views match.
 */
export const draftStatusVariant = (status?: string | null): string => {
  switch (status) {
    case 'draft_rejected':
      return 'danger';
    case 'draft_approved':
      return 'success';
    case 'draft_sent':
      return 'info';
    case 'signed_by_both':
      return 'success';
    default:
      // signed_by_company / signed_by_person and any other signed state.
      return 'primary';
  }
};

/**
 * Draft/signing status badge, or null when there is no draft ("none").
 * Label comes from the shared `contracts.draftStatus.*` i18n keys.
 */
export const draftStatusBadge = (
  status: string | null | undefined,
  t: (key: string) => string
): ContractBadge | null => {
  if (!status || status === 'none') return null;
  return { label: t(`contracts.draftStatus.${status}`), variant: draftStatusVariant(status) };
};
